import { i18n } from "@lingui/core"
import { t } from "@lingui/macro"
import { PaxPrice, PriceDetailPromo, Promo } from '@ssc-booking/api-contracts';
import * as Amount from '@ssc-booking/api-contracts/src/amount';
import * as F from "fp-ts/function"
import * as O from "fp-ts/Option"
import { Option } from "fp-ts/Option"
import * as ROA from "fp-ts/ReadonlyArray"
import * as P from "fp-ts/Predicate"
import * as RONEA from "fp-ts/ReadonlyNonEmptyArray"
import { ReadonlyNonEmptyArray } from "fp-ts/ReadonlyNonEmptyArray"
import * as ORD from "fp-ts/Ord"
import * as S from "fp-ts/string"
import * as Tuple from "fp-ts/Tuple"
import { formatAmount } from "../../../app-core/_common/format/localization"
import { CardPromoBadgesContent } from "../../../app-core/_common/sanity/sanity-types"
import { bulletRow, labelRow, paxRow, ReceiptSectionWithLabel } from "../types"
import { emptyRows, guestLabel, isDiscount, PriceDetailPromoEqByValues, PriceDetailPromoSemigroup } from "./utils"
import { isVsSalesPromo } from "./vsSales"

export type PromoLabels = Record<Promo, string>
type PaxPromo = [paxPrice: PaxPrice, promo: PriceDetailPromo]

export const summaryForPromotions = (
    paxPrices: readonly PaxPrice[],
    promoLabels: PromoLabels,
    promoBadges: readonly CardPromoBadgesContent[],
): readonly ReceiptSectionWithLabel[] =>
    F.pipe(
        paxPrices,
        ROA.chain(joinPromoDetails),
        ROA.sort(byConfigurationCode),
        ROA.matchW(
            emptyRows,
            F.flow(RONEA.group(byConfigurationCode), ROA.chain(receiptSections(promoLabels, promoBadges))),
        ),
    )

const joinPromoDetails = (paxPrice: PaxPrice) =>
    F.pipe(
        paxPrice.details.promotions,
        ROA.filter(P.not(isVsSalesPromo)),
        ROA.map((promo): PaxPromo => [paxPrice, promo] as const),
    )

const receiptSections =
    (promoLabels: PromoLabels, promoBadges: readonly CardPromoBadgesContent[]) =>
    (paxPromos: ReadonlyNonEmptyArray<PaxPromo>) =>
        F.pipe(
            RONEA.head(paxPromos)[1].configurationCode,
            O.match(
                receiptSectionsWithoutConfigurationCode(promoLabels, paxPromos),
                receiptSectionsWithConfigurationCode(promoLabels, promoBadges, paxPromos),
            ),
        )

const receiptSectionsWithoutConfigurationCode =
    (promoLabels: PromoLabels, paxPromos: ReadonlyNonEmptyArray<PaxPromo>) => (): readonly ReceiptSectionWithLabel[] =>
        F.pipe(paxPromos, RONEA.sort(byPromo), RONEA.group(byPromo), ROA.map(receiptSectionByPromoType(promoLabels)))

const receiptSectionByPromoType =
    (promoLabels: PromoLabels) =>
    (paxPromos: ReadonlyNonEmptyArray<PaxPromo>): ReceiptSectionWithLabel => {
        const promos = F.pipe(paxPromos, RONEA.map(Tuple.snd))
        const headPromo = RONEA.head(promos)
        const overallPromo = F.pipe(promos, RONEA.concatAll(PriceDetailPromoSemigroup))

        if (headPromo.promo === "KidsSaving") return receiptSectionKidSavings(overallPromo, promoLabels)

        const allPromosAreEqual = F.pipe(promos, ROA.uniq(PriceDetailPromoEqByValues)).length === 1

        if (allPromosAreEqual) return receiptSectionByPromoTypeNoDetails(overallPromo, promoLabels)
        return receiptSectionByPromoTypeWithDetails(overallPromo, promoLabels, paxPromos)
    }

const receiptSectionKidSavings = (
    overallPromo: Pick<PriceDetailPromo, "promo" | "discount">,
    promoLabels: PromoLabels,
): ReceiptSectionWithLabel => ({
    key: overallPromo.promo,
    label: labelByPromo(overallPromo, promoLabels),
    isPromotional: isDiscount(overallPromo.discount),
    totalAmount: O.some(overallPromo.discount),
    rows: [labelRow(i18n._(t({ id: `kids-savings-promo-disclaimer` })))],
})

const receiptSectionByPromoTypeNoDetails = (
    overallPromo: Pick<PriceDetailPromo, "promo" | "configurationCode" | "discount" | "percentage" | "credit">,
    promoLabels: PromoLabels,
) => ({
    key: overallPromo.promo,
    label: prefixedLabelFor(overallPromo, promoLabels, []),
    isPromotional: isDiscount(overallPromo.discount),
    totalAmount: O.some(overallPromo.discount),
    rows: [],
})

const receiptSectionByPromoTypeWithDetails = (
    overallPromo: Pick<PriceDetailPromo, "promo" | "discount">,
    promoLabels: PromoLabels,
    paxPromos: ReadonlyNonEmptyArray<PaxPromo>,
) => ({
    key: overallPromo.promo,
    label: labelByPromo(overallPromo, promoLabels),
    isPromotional: isDiscount(overallPromo.discount),
    totalAmount: O.none,
    rows: F.pipe(
        paxPromos,
        RONEA.map(([paxPrice, promo]) =>
            paxRow({
                label: paxLabelFor(paxPrice, promo, promoLabels),
                isPromotional: isDiscount(promo.discount),
                amount: O.some(promo.discount),
            }),
        ),
    ),
})

const receiptSectionsWithConfigurationCode =
    (
        promoLabels: PromoLabels,
        promoBadges: readonly CardPromoBadgesContent[],
        paxPromos: ReadonlyNonEmptyArray<PaxPromo>,
    ) =>
    (configurationCode: string): readonly ReceiptSectionWithLabel[] => {
        const rows = F.pipe(
            paxPromos,
            RONEA.sort(byPromo),
            RONEA.group(byPromo),
            ROA.map(rowBulletForPromo(promoLabels)),
        )
        const isPromotional = F.pipe(
            rows,
            ROA.every((r) => r.isPromotional),
        )
        return [
            {
                key: configurationCode,
                label: labelFor(
                    { configurationCode: O.some(configurationCode), promo: "Generic" },
                    promoLabels,
                    promoBadges,
                ),
                totalAmount: O.none,
                isPromotional,
                rows,
            },
        ]
    }

const rowBulletForPromo = (promoLabels: PromoLabels) => (paxPromos: ReadonlyNonEmptyArray<PaxPromo>) => {
    const promos = F.pipe(paxPromos, RONEA.map(Tuple.snd))
    const overallPromo = F.pipe(promos, RONEA.concatAll(PriceDetailPromoSemigroup))

    const allPromosAreEqual = F.pipe(promos, ROA.uniq(PriceDetailPromoEqByValues)).length === 1

    if (allPromosAreEqual)
        return bulletRow({
            label: prefixedLabelFor(overallPromo, promoLabels, []),
            isPromotional: isDiscount(overallPromo.discount),
            amount: O.some(overallPromo.discount),
            rows: [],
        })

    return bulletRow({
        label: labelFor(overallPromo, promoLabels, []),
        isPromotional: isDiscount(overallPromo.discount),
        amount: O.none,
        rows: F.pipe(
            paxPromos,
            RONEA.map(([paxPrice, promo]) =>
                paxRow({
                    label: paxLabelFor(paxPrice, promo, promoLabels),
                    isPromotional: isDiscount(promo.discount),
                    amount: O.some(promo.discount),
                }),
            ),
        ),
    })
}

export const prefixedLabelFor = (
    promo: Pick<PriceDetailPromo, "promo" | "configurationCode" | "percentage" | "credit">,
    promoLabels: PromoLabels,
    promoBadges: readonly CardPromoBadgesContent[],
) => {
    const title = labelFor(promo, promoLabels, promoBadges)
    return F.pipe(
        withPercentage(promo.percentage, title),
        O.alt(() => withCredit(promo.credit, title)),
        O.getOrElse(F.constant(title)),
    )
}

const labelFor = (
    promo: Pick<PriceDetailPromo, "promo" | "configurationCode">,
    promoLabels: PromoLabels,
    promoBadges: readonly CardPromoBadgesContent[],
) =>
    F.pipe(
        labelFromPromoBadges(promoBadges, promo.configurationCode),
        O.getOrElseW(() => labelByPromo(promo, promoLabels)),
    )

const paxLabelFor = (
    paxPrice: PaxPrice,
    promo: Pick<PriceDetailPromo, "promo" | "configurationCode" | "percentage" | "credit">,
    promoLabels: PromoLabels,
) => {
    const realBaseLabel = guestLabel(paxPrice.number)
    return F.pipe(
        withCredit(promo.credit, realBaseLabel),
        O.match(
            () => realBaseLabel,
            () => `${realBaseLabel} - ${prefixedLabelFor(promo, promoLabels, [])}`,
        ),
    )
}

const labelByPromo = (promo: Pick<PriceDetailPromo, "promo">, promoLabels: PromoLabels) =>
    i18n._(promoLabels[promo.promo])

const labelFromPromoBadges = (promoBadges: readonly CardPromoBadgesContent[], configurationCode: Option<string>) =>
    F.pipe(
        configurationCode,
        O.chain(matchingPromoBadge(promoBadges)),
        O.chain((c) => O.fromNullable(c.content?.title)),
    )

const matchingPromoBadge = (promoBadges: readonly CardPromoBadgesContent[]) => (code: string) =>
    F.pipe(promoBadges, ROA.findFirst(byCode(code)))

const byCode = (code: string) => (promo: CardPromoBadgesContent) => promo.code === code

const withPercentage = (percentage: O.Option<number>, title: string) =>
    F.pipe(
        percentage,
        O.map((percentage) => `${percentage}% ${title}`),
    )

const withCredit = (credit: O.Option<Amount.Amount>, title: string) =>
    F.pipe(
        credit,
        O.map((credit) => `${formatAmount(credit)} ${title}`),
    )

const byConfigurationCode = F.pipe(
    O.getOrd(S.Ord),
    ORD.contramap((x: PaxPromo) => {
        return x[1].configurationCode
    }),
)

const byPromo = F.pipe(
    S.Ord,
    ORD.contramap((x: PaxPromo) => x[1].promo),
)
